/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  User,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";

const ContractDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { fetchContractById } = useContext(AdminContext);

  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadContract = async () => {
      setIsLoading(true);
      try {
        const result = await fetchContractById(contractId);
        const contractData = result?.contract || result?.data;
        if (result?.success && contractData) {
          setNotFound(false);
          setContract(contractData);
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    loadContract();
  }, [contractId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (notFound || !contract) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-xl font-bold text-gray-600 dark:text-gray-400">
          Contract not found
        </p>
        <button
          onClick={() => navigate("/contracts")}
          className="px-4 py-2 bg-primary text-white rounded-lg"
        >
          Back to Contracts
        </button>
      </div>
    );
  }

  const getStatusStyle = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800";
      case "PAUSED":
        return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800";
      case "TERMINATED":
        return "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800";
      case "COMPLETED":
        return "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getPayoutStatusStyle = (status) => {
    switch (status) {
      case "PAID":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400";
      case "PENDING":
        return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "PROCESSING":
        return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400";
      case "FAILED":
        return "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400";
      case "CANCELLED":
        return "bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const client = contract.client || contract.user || {};
  const platformPercentage = 20;
  const caregiverPercentage = 80;
  const clientPayment = Number(contract.monthlySalary) || 0;
  const chapaFee = Number((clientPayment * 0.02875).toFixed(2));
  const netAfterChapaFee = Number((clientPayment - chapaFee).toFixed(2));
  const caregiverReceives = Number((netAfterChapaFee * 0.8).toFixed(2));
  const platformShare = Number((netAfterChapaFee - caregiverReceives).toFixed(2));
  const terminationReason =
    contract.terminationReason || contract.terminationNote;

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/contracts")}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-text dark:text-dark-text"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Contract Details
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            View contract information and payout history
          </p>
        </div>
      </div>

      {/* Contract Info Card */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-text dark:text-dark-text">
            Contract Information
          </h2>
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusStyle(
              contract.status,
            )}`}
          >
            {contract.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Client Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase">
              Client
            </h3>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <User size={20} className="text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="font-bold text-text dark:text-dark-text">
                  {client.name || "N/A"}
                </p>
                <p className="text-sm text-gray-500 dark:text-dark-muted">
                  {client.email || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Caregiver Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase">
              Caregiver
            </h3>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <User size={20} className="text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="font-bold text-text dark:text-dark-text">
                  {contract.caregiver?.name || "N/A"}
                </p>
                <p className="text-sm text-gray-500 dark:text-dark-muted">
                  {contract.caregiver?.phoneNumber || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Financial Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase">
              Financial Details
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-dark-muted">
                  Monthly Salary (Client Pays)
                </span>
                <span className="font-bold text-text dark:text-dark-text">
                  Br {clientPayment.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-dark-muted">
                  Chapa Fee
                </span>
                <span className="font-bold text-text dark:text-dark-text">
                  Br {chapaFee.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} (2.875%)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-dark-muted">
                  Net After Chapa Fee
                </span>
                <span className="font-bold text-text dark:text-dark-text">
                  Br {netAfterChapaFee.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-dark-muted">
                  Platform Share
                </span>
                <span className="font-bold text-text dark:text-dark-text">
                  Br {platformShare.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} ({platformPercentage}%)
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-dark-border">
                <span className="text-sm font-medium text-gray-600 dark:text-dark-muted">
                  Caregiver Receives
                </span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">
                  Br {caregiverReceives.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase">
              Timeline
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-dark-muted">
                  Start Date:
                </span>
                <span className="font-medium text-text dark:text-dark-text">
                  {contract.startDate
                    ? new Date(contract.startDate).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
              {contract.endDate && (
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-dark-muted">
                    End Date:
                  </span>
                  <span className="font-medium text-text dark:text-dark-text">
                    {new Date(contract.endDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Termination Info */}
        {contract.status === "TERMINATED" && terminationReason && (
          <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle
                className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                size={20}
              />
              <div>
                <h4 className="font-bold text-red-900 dark:text-red-200 mb-1">
                  Termination Reason
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {terminationReason}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {contract.status === "ACTIVE" &&
        contract.paymentSummary?.hasUnpaidBill && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-900/20">
            <div className="flex items-start gap-3">
              <AlertCircle
                className="mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400"
                size={22}
              />
              <div>
                <h2 className="font-bold text-amber-900 dark:text-amber-200">
                  Unpaid bill
                </h2>
                <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                  The client has an outstanding monthly salary payment. The
                  client must pay the bill before the caregiver receives this
                  month&apos;s earnings.
                </p>
                {contract.paymentSummary.nextPayablePeriod && (
                  <p className="mt-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Amount due: Br{" "}
                    {Number(
                      contract.paymentSummary.nextPayablePeriod.amount || 0,
                    ).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Payout History */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-xl font-bold text-text dark:text-dark-text">
            Payout History
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 font-bold tracking-wider">Month</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Gross Amount
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Platform Share
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Caregiver Amount
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Status</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Paid Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {contract.payouts && contract.payouts.length > 0 ? (
                contract.payouts.map((payout) => (
                  <tr
                    key={payout.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <span className="font-medium text-text dark:text-dark-text">
                        {payout.month
                          ? new Date(payout.month).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                            })
                          : "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-text dark:text-dark-text">
                        Br {payout.grossAmount?.toLocaleString() || "0"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 dark:text-dark-muted">
                        Br {Number(
                          payout.platformFee ?? payout.platformAmount ?? 0,
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        Br {Number(
                          payout.caregiverAmount ?? 0,
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getPayoutStatusStyle(
                          payout.status,
                        )}`}
                      >
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 dark:text-dark-muted">
                        {payout.paidAt
                          ? new Date(payout.paidAt).toLocaleDateString()
                          : "-"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    No payout history available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContractDetail;
